import { Link } from "react-router-dom";
import Reveal, { RevealItem } from "./Reveal";
import { useContentItems } from "@/hooks/useContentItems";
import {
  CONTENT_KIND_SINGULAR,
  detailHref,
  type ContentItem,
} from "@/lib/insights";

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

const InsightsPreview = () => {
  const media = useContentItems({ kind: "webinar", limit: 1 });
  const blog = useContentItems({ kind: "blog", limit: 1 });
  const news = useContentItems({ kind: "news", limit: 1 });
  const isLoading = media.isLoading || blog.isLoading || news.isLoading;
  const items: ContentItem[] = [
    media.data?.[0],
    blog.data?.[0],
    news.data?.[0],
  ].filter((x): x is ContentItem => Boolean(x));
  const showGrid = isLoading || items.length > 0;

  return (
    <section className="bg-background py-section-tight">
      <div className="container">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="eyebrow">Insights</p>
            <h2 className="display mt-5 text-section-title text-foreground">
              From the front line of clinical AI.
            </h2>
          </div>
          <Link
            to="/insights"
            className="group inline-flex items-center gap-2 text-foreground font-medium border-b border-foreground/30 pb-1 hover:border-primary transition-colors duration-300 self-start md:self-auto"
          >
            Explore all insights
            <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </Reveal>

        {showGrid && (
          <Reveal
            variant="stagger"
            stagger={0.1}
            className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border"
          >
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <RevealItem key={i}>
                    <div className="bg-background p-card-standard flex flex-col h-full min-h-[280px]">
                      <div className="flex items-center justify-between text-xs">
                        <span className="h-3 w-20 bg-muted rounded animate-pulse" />
                        <span className="h-3 w-16 bg-muted rounded animate-pulse" />
                      </div>
                      <div className="mt-10 space-y-2 flex-1">
                        <div className="h-5 w-full bg-muted rounded animate-pulse" />
                        <div className="h-5 w-3/4 bg-muted rounded animate-pulse" />
                      </div>
                    </div>
                  </RevealItem>
                ))
              : items.map((item) => (
                  <RevealItem key={item.id}>
                    <Link
                      to={detailHref(item)}
                      className="group bg-background p-card-standard flex flex-col h-full min-h-[280px] hover:bg-foreground/[0.02] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="uppercase tracking-eyebrow text-primary font-medium">
                          {item.kind === "blog" ? "Blog" : CONTENT_KIND_SINGULAR[item.kind]}
                        </span>
                        <span className="font-mono text-muted-foreground">
                          {formatDate(item.published_at)}
                        </span>
                      </div>

                      <h3 className="mt-10 font-display text-[22px] md:text-[24px] leading-[1.2] text-foreground tracking-tight flex-1">
                        {item.title}
                      </h3>

                      <div className="mt-8 flex items-center justify-between text-sm text-muted-foreground">
                        <span className="text-foreground font-medium">
                          {item.kind === "webinar" ? "Watch now" : "Read now"}
                        </span>
                        <span
                          aria-hidden
                          className="transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1 text-foreground"
                        >
                          →
                        </span>
                      </div>
                    </Link>
                  </RevealItem>
                ))}
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default InsightsPreview;
